import "./suppress-console";
import axios from "axios";
import VueAxios from "vue-axios";
import VueDatePicker from "@vuepic/vue-datepicker";
import "vue-multiselect/dist/vue-multiselect.css";
import "@vuepic/vue-datepicker/dist/main.css";
import "floating-vue/dist/style.css";

import "@/styles/variables.css";
import "@/styles/base.css";
import "@/styles/castr.css";
import "@/styles/main.css";

import { createApp } from "vue";
import filters from "@/filters";
import router from "@/router";
import store from "@/store";
import App from "@/App";
import { initAnalytics, track } from "@/lib/tracking";
// import api from '@/api';
// Vue.prototype.axios = api;
import * as Sentry from "@sentry/vue";

import StreamService from "@/services/StreamService";

// self-hoster not used so far
// import { activateSentry } from '@/lib/analytics';

import VueClipboard from "vue3-clipboard";
import Notifications from "@kyvg/vue3-notification";
//TODO MIGRATION
import FloatingVue from "floating-vue";
import { showModal, hideModal } from "@/Utils/modalBus";
import { clickOutside } from "@/directives/clickOutside";

// import VueCookie from 'vue-cookie';

// Auto-login token hand-off (Google SSO, invite-via-Google, any a_token link)
// lands on /app/login?a_token=<JWT> via a full page load. Persist the token
// synchronously BEFORE the app mounts so the shell's bootstrap requests are
// authenticated from the very first call. The axios request interceptor
// (src/api.js) reads localStorage.token live per request, so writing it here is
// enough. Without this, App.vue's initializeApp / MainSidebar fire authed calls
// before SignIn.vue's tryAutoLogin sets the token, 401, and bounce to login.
// SignIn.vue still runs tryAutoLogin to fetch the profile and redirect.
try {
  const aToken = new URLSearchParams(window.location.search).get("a_token");
  if (aToken) {
    // A fresh a_token landing (Google SSO) is a new login. Clear any stale
    // user/2FA state from a prior session so the router guard does not see a
    // stale `user.twoFactorEnabled` and strip the freshly-set token, which
    // bounced returning 2FA-enabled users back to the login form.
    localStorage.removeItem("user");
    localStorage.removeItem("twoFactorRequired");
    localStorage.setItem("token", aToken);
  }
} catch (e) {
  // no-op: URL/localStorage unavailable
}

const app = createApp(App);

if (process.env.VUE_APP_API_ENV === "prod") {
  app.config.warnHandler = () => {};
  app.config.errorHandler = () => {};
}

// Sentry.init({
//   app,
//   dsn: "https://b9c85713082d2caaf203d5cd0ef4848b@o373065.ingest.us.sentry.io/4509509929074688",
//   // Setting this option to true will send default PII data to Sentry.
//   // For example, automatic IP address collection on events
//   sendDefaultPii: true,
//   integrations: [Sentry.replayIntegration()]
// });

app.config.globalProperties.$filters = {
  bytes(num, breakSegments, decimalPlaces = 3, mini = false) {
    // jacked from: https://github.com/sindresorhus/pretty-bytes
    if (typeof num !== "number" || isNaN(num)) {
      throw new TypeError("Expected a number");
    }

    const neg = num < 0;
    const units = [
      mini ? "B" : "Bytes",
      "kB",
      "MB",
      "GB",
      "TB",
      "PB",
      "EB",
      "ZB",
      "YB",
    ];

    if (neg) num = -num;
    if (num < 1) {
      const value = (neg ? "-" : "") + num;
      return breakSegments ? { value, unit: units[0] } : value + " Bytes";
    }

    const exponent = Math.min(
      Math.floor(Math.log(num) / Math.log(1000)),
      units.length - 1,
    );
    const unit = units[exponent].toLowerCase();

    num = (num / Math.pow(1000, exponent)).toFixed(decimalPlaces) * 1;

    const value = (neg ? "-" : "") + num;
    return breakSegments ? { value: Number(value), unit } : value + " " + unit;
  },
};

app.use(VueClipboard, {
  autoSetContainer: true,
  appendToBody: true,
});
app.use(Notifications);
app.config.globalProperties.$showModal = showModal;
app.config.globalProperties.$hideModal = hideModal;
app.directive("click-outside", clickOutside);
app.use(FloatingVue);
//TODO MIGRATION
// Vue.use(ToggleButton);
app.use(VueAxios, axios);
// app.use(VueCookie);
app.use(store);
app.use(router);
// filters(Vue);
app.component("VueDatePicker", VueDatePicker);
// Regions are now loaded in App.vue initialization

// Make store globally accessible for cache clearing
window.$store = store;

// activateSentry();
// Product analytics (PostHog, manual-first). Repoint the legacy global
// trackEvent hook (previously a no-op) so existing call sites emit real events.
initAnalytics();
window.trackEvent = (name, props) => track(name, props);

// Vue.config.productionTip = false;

/* eslint-disable no-new */
// Wait for the initial route to resolve before mounting so route.meta (e.g.
// fullScreen on /app/login) is correct on first paint. This stops MainSidebar
// (v-if="!route.meta.fullScreen") from briefly mounting on auth routes and
// firing an authed getinfoMember call during the a_token landing.
// Mount once the router is ready, but never block forever: if the initial
// navigation hangs (e.g. a guard that doesn't call next), still mount after a
// short timeout so the app's own auth handling can run and redirect. Restores
// the pre-router.isReady() safety net without touching auth/guard logic.
Promise.race([
  router.isReady(),
  new Promise((resolve) => setTimeout(resolve, 1500)),
]).finally(() => {
  app.mount("#app");
});
